import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, 
  ArrowRightLeft,
  Save,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StockTransfer = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [fromSLocs, setFromSLocs] = useState([]);
  const [toSLocs, setToSLocs] = useState([]);
  const [sourceStock, setSourceStock] = useState(null);
  
  const [formData, setFormData] = useState({
    material_id: '',
    from_plant: '',
    from_storage_location: '',
    from_stock_type: 'UNRESTRICTED',
    to_plant: '',
    to_storage_location: '',
    to_stock_type: 'UNRESTRICTED',
    quantity: '',
    unit_of_measure: 'EA',
    posting_date: new Date().toISOString().split('T')[0],
    document_date: new Date().toISOString().split('T')[0],
    header_text: 'Stock Transfer',
    reference: '',
    created_by: 'ADMIN'
  });
  
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    if (formData.from_plant) {
      const filtered = storageLocations.filter(s => s.plant_id === formData.from_plant);
      setFromSLocs(filtered);
    }
  }, [formData.from_plant, storageLocations]);

  useEffect(() => {
    if (formData.to_plant) {
      const filtered = storageLocations.filter(s => s.plant_id === formData.to_plant);
      setToSLocs(filtered);
    }
  }, [formData.to_plant, storageLocations]);

  useEffect(() => {
    if (formData.material_id && formData.from_plant && formData.from_storage_location) {
      loadSourceStock();
    }
  // eslint-disable-next-line
  }, [formData.material_id, formData.from_plant, formData.from_storage_location]);

  const loadMasterData = async () => {
    try {
      setLoading(true);
      const [matRes, plantRes, slocRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/materials`),
        axios.get(`${BACKEND_URL}/api/material-management/plants`),
        axios.get(`${BACKEND_URL}/api/material-management/storage-locations`)
      ]);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
      setStorageLocations(slocRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const loadSourceStock = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/stock-balance/${formData.material_id}/${formData.from_plant}?storage_location=${formData.from_storage_location}`
      );
      const stock = response.data?.[0];
      setSourceStock(stock);
      if (stock) {
        setFormData(prev => ({ ...prev, unit_of_measure: stock.base_unit || 'EA' }));
      }
    } catch (error) {
      setSourceStock(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.material_id || !formData.from_plant || !formData.from_storage_location ||
        !formData.to_plant || !formData.to_storage_location) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error('Quantity must be greater than zero');
      return;
    }
    
    if (formData.from_plant === formData.to_plant && 
        formData.from_storage_location === formData.to_storage_location) {
      toast.error('Source and destination must be different');
      return;
    }
    
    const availableQty = sourceStock?.unrestricted_stock || 0;
    if (parseFloat(formData.quantity) > availableQty) {
      toast.error(`Insufficient stock. Available: ${availableQty}`);
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        quantity: parseFloat(formData.quantity)
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/stock-transfer`, payload);
      setResult(response.data);
      toast.success(`Transfer posted! Document: ${response.data.material_document}`);
    } catch (error) {
      console.error('Error posting transfer:', error);
      toast.error(error.response?.data?.detail || 'Failed to post transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      material_id: '',
      from_plant: '',
      from_storage_location: '',
      from_stock_type: 'UNRESTRICTED',
      to_plant: '',
      to_storage_location: '',
      to_stock_type: 'UNRESTRICTED',
      quantity: '',
      unit_of_measure: 'EA',
      posting_date: new Date().toISOString().split('T')[0],
      document_date: new Date().toISOString().split('T')[0],
      header_text: 'Stock Transfer',
      reference: '',
      created_by: 'ADMIN'
    });
    setSourceStock(null);
    setResult(null);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(num || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-2">Loading master data...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/inventory')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stock Transfer</h1>
            <p className="text-sm text-gray-500">MIGO - Movement Type 301/311</p>
          </div>
        </div>

        {/* Success Result */}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-800">Transfer Posted Successfully</h3>
                <div className="mt-2 text-sm text-green-700 space-y-1">
                  <p><strong>Material Document:</strong> {result.material_document}</p>
                  <p><strong>Fiscal Year:</strong> {result.fiscal_year}</p>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleReset}>
                    New Transfer
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/inventory/stock-overview')}>
                    View Stock
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow p-6">
            {/* Material Selection */}
            <div className="mb-6">
              <Label className="text-sm font-medium">Material *</Label>
              <select
                className="w-full border rounded-md px-3 py-2 mt-1"
                value={formData.material_id}
                onChange={(e) => setFormData({ ...formData, material_id: e.target.value })}
                required
              >
                <option value="">Select Material</option>
                {materials.map(m => (
                  <option key={m.material_number} value={m.material_number}>
                    {m.material_number} - {m.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FROM Section */}
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h3 className="font-medium text-red-800 mb-4 flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" /> From (Source)
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Plant *</Label>
                    <select
                      className="w-full border rounded-md px-3 py-2 mt-1"
                      value={formData.from_plant}
                      onChange={(e) => setFormData({ ...formData, from_plant: e.target.value, from_storage_location: '' })}
                      required
                    >
                      <option value="">Select Plant</option>
                      {plants.map(p => (
                        <option key={p.plant_id} value={p.plant_id}>
                          {p.plant_id} - {p.plant_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Storage Location *</Label>
                    <select
                      className="w-full border rounded-md px-3 py-2 mt-1"
                      value={formData.from_storage_location}
                      onChange={(e) => setFormData({ ...formData, from_storage_location: e.target.value })}
                      required
                      disabled={!formData.from_plant}
                    >
                      <option value="">Select Storage Location</option>
                      {fromSLocs.map(s => (
                        <option key={s.storage_location_id} value={s.storage_location_id}>
                          {s.storage_location_id} - {s.storage_location_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Available Stock */}
                  {sourceStock && (
                    <div className="bg-white rounded p-3 border">
                      <p className="text-sm text-gray-600">Available Stock:</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatNumber(sourceStock.unrestricted_stock)} {sourceStock.base_unit}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* TO Section */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-medium text-green-800 mb-4 flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" /> To (Destination)
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Plant *</Label>
                    <select
                      className="w-full border rounded-md px-3 py-2 mt-1"
                      value={formData.to_plant}
                      onChange={(e) => setFormData({ ...formData, to_plant: e.target.value, to_storage_location: '' })}
                      required
                    >
                      <option value="">Select Plant</option>
                      {plants.map(p => (
                        <option key={p.plant_id} value={p.plant_id}>
                          {p.plant_id} - {p.plant_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium">Storage Location *</Label>
                    <select
                      className="w-full border rounded-md px-3 py-2 mt-1"
                      value={formData.to_storage_location}
                      onChange={(e) => setFormData({ ...formData, to_storage_location: e.target.value })}
                      required
                      disabled={!formData.to_plant}
                    >
                      <option value="">Select Storage Location</option>
                      {toSLocs.map(s => (
                        <option key={s.storage_location_id} value={s.storage_location_id}>
                          {s.storage_location_id} - {s.storage_location_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Stock Type</Label>
                    <select
                      className="w-full border rounded-md px-3 py-2 mt-1"
                      value={formData.to_stock_type}
                      onChange={(e) => setFormData({ ...formData, to_stock_type: e.target.value })}
                    >
                      <option value="UNRESTRICTED">Unrestricted</option>
                      <option value="QUALITY">Quality Inspection</option>
                      <option value="BLOCKED">Blocked</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Quantity and Dates */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <div>
                <Label className="text-sm font-medium">Quantity *</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="Enter quantity"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">UoM</Label>
                <Input
                  value={formData.unit_of_measure}
                  onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                  className="mt-1"
                  readOnly
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Posting Date</Label>
                <Input
                  type="date"
                  value={formData.posting_date}
                  onChange={(e) => setFormData({ ...formData, posting_date: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Reference</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="External ref"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Movement Type Info */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Movement Type:</strong> {
                  formData.from_plant === formData.to_plant 
                    ? '311 - Transfer within Plant' 
                    : '301 - Transfer between Plants'
                }
              </p>
            </div>

            {/* Submit Buttons */}
            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleReset}>
                Reset
              </Button>
              <Button type="submit" disabled={submitting} className="bg-orange-600 hover:bg-orange-700">
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Posting...</>
                ) : (
                  <><ArrowRightLeft className="h-4 w-4 mr-2" /> Post Transfer</>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default StockTransfer;
